import api from 'http';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import React from 'react';
import { FiEdit2, FiClock } from 'react-icons/fi';
import FullPageLoader from 'Components/FullPageLoader/FullPageLoader';
import Show from 'Components/Show/Show';
import { BiSave } from 'react-icons/bi';
import FormatMomentDate from 'Utils/FormatMomentDate';
import { RxCross2 } from 'react-icons/rx';
import CustomTextarea from 'Common/CustomTextArea';
import PaymentNoteHistoryPopup from 'Popups/PaymentNoteHistoryPopup/PaymentNoteHistoryPopup';
import UserIcon from 'SVGS/UserIcon';
import ClockIcon from 'SVGS/ClockIcon';

const initialNoteModel = {
  practiceId: 0,
  addedBy: "",
  addedDate: "2022-02-08",
  updatedBy: "",
  updatedDate: "",
  inActive: false,
  inActiveReason: "",
  inActiveBy: "",
  inActiveDate: "",
  id: 0,
  PaymentMainId: "",
  type: "",
  notes: "",
  isAlert: false,
  alertStartDate: "",
  alertEndDate: ""
};

const PaymentNotes = ({ paymentId, setNotes, notes, setNotesCount }) => {
  const [addNotes, setAddNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showAddendumPopup, setShowAddendumPopup] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [errors, setErrors] = useState({
    addNoteError: '',
    editNoteError: ''
  });

  // Edit Note Handler
  const startEditing = (note) => {
    setEditingNoteId(note.id);
    setEditingText(note.notes);
    setErrors((prev) => ({ ...prev, editNoteError: '' }));
  };

  const handleCancelClick = () => {
    setEditingNoteId(null);
    setEditingText('');
    setErrors((prev) => ({ ...prev, editNoteError: '' }));
  };

  const handleSaveClick = async (id) => {
    // reset errors
    setErrors({ addNoteError: '', editNoteError: '' });

    // Validation
    if (!id && newNoteText.trim() === '') {
      setErrors((prev) => ({ ...prev, addNoteError: 'Note is required' }));
      return;
    }
    if (id && editingText.trim() === '') {
      setErrors((prev) => ({ ...prev, editNoteError: 'Note is required' }));
      return;
    }

    let payload = {
      ...initialNoteModel,
      notes: newNoteText,
      PaymentMainId: paymentId,
    };

    if (id) {
      const noteToUpdate = notes.find((n) => n.id === id);
      if (noteToUpdate) {
        payload = {
          ...noteToUpdate,
          notes: editingText,
          PaymentMainId: paymentId,
        };
      }
    }

    try {
      setLoading(true);
      const response = await api.post(
        `${process.env.REACT_APP_TC_PMS}/api/patientpayment/v2/SavePaymentNotes`,
        payload
      );
      toast.success(response?.data?.responseMessage);
      await getNotes();
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoading(false);
      setNewNoteText('');
      setEditingNoteId(null);
      setEditingText('');
      setAddNotes(false);
    }
  };

  const cancelAdding = () => {
    setAddNotes(false);
    setNewNoteText('');
    setErrors((prev) => ({ ...prev, addNoteError: '' }));
  };

  const getNotes = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `${process.env.REACT_APP_TC_PMS}/api/patientpayment/v2/FindPaymentNotes?PaymentMainid=${paymentId}`
      );
      setNotes(response?.data.dataModel);
      setNotesCount(response?.data.dataModel.length);
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleAddTrigger = () => setAddNotes(true);
    window.addEventListener('triggerAddNote', handleAddTrigger);
    return () => window.removeEventListener('triggerAddNote', handleAddTrigger);
  }, []);

  const handleNewNoteChange = (e) => {
    const val = e?.target?.value ?? '';
    setNewNoteText(val.length > 500 ? val.slice(0, 500) : val);
  };

  const handleEditTextChange = (e) => {
    const val = e?.target?.value ?? '';
    setEditingText(val.length > 500 ? val.slice(0, 500) : val);
  };

  const handleHistoryClick = (noteId) => {
    setSelectedNoteId(noteId);
    setShowAddendumPopup(true);
  };

  const handleCloseAddendumPopup = () => {
    setShowAddendumPopup(false);
    setSelectedNoteId(null);
  };

  return (
    <div className="space-y-3">
      {showAddendumPopup && selectedNoteId && (
        <PaymentNoteHistoryPopup
          onClose={handleCloseAddendumPopup}
          disableDraggable={false}
          paymentMainNotesId={selectedNoteId}
        />
      )}
      <Show when={loading}>
        <FullPageLoader allowFullScreen />
      </Show>

      {addNotes && (
        <div className="p-3 space-y-2 bg-white border rounded-lg border-slate-200">
          <CustomTextarea
            value={newNoteText}
            onChange={handleNewNoteChange}
            placeholder="Enter your note..."
            extraClass="w-full resize-none text-sm"
            rows={3}
            autoFocus
            maxWidth="100%"
          />
          {errors.addNoteError && (
            <div className="text-xs text-red-500">{errors.addNoteError}</div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveClick()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg transition-colors"
              >
                <BiSave className="w-3.5 h-3.5" />
                Save
              </button>
              <button
                onClick={cancelAdding}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <RxCross2 className="text-md" />
                Cancel
              </button>
            </div>
            <div className="text-sm text-slate-500">
              <span>{newNoteText?.length}</span>
              <span>/ 500</span>
            </div>
          </div>
        </div>
      )}

      {notes?.length === 0 && !addNotes ? (
        <div className="py-4 text-sm text-center text-slate-500">
          No notes yet. Click "Add" to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {notes?.map((note) => (
            <div
              key={note.id}
              className="p-3 space-y-2 bg-white border rounded-lg border-slate-200"
            >
              {editingNoteId === note.id ? (
                <>
                  <CustomTextarea
                    value={editingText}
                    onChange={handleEditTextChange}
                    extraClass="w-full resize-none text-sm"
                    rows={3}
                    autoFocus
                    maxWidth="100%"
                  />
                  {errors.editNoteError && (
                    <div className="text-xs text-red-500">{errors.editNoteError}</div>
                  )}
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveClick(note.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg transition-colors"
                      >
                        <BiSave className="w-3.5 h-3.5" />
                        Save
                      </button>
                      <button
                        onClick={handleCancelClick}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        <RxCross2 className="text-md" />
                        Cancel
                      </button>
                    </div>
                    <div className="text-sm text-slate-500">
                      <span>{editingText?.length}</span>
                      <span>/ 500</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <p className="flex-1 text-sm text-slate-700">
                      <Show when={note.type}>
                        <span className={`inline-flex items-center px-3 py-1 mt-1 mr-1 rounded text-xs font-semibold bg-amber-100 text-amber-700  `} >
                          {note.type}
                        </span>
                      </Show>
                      {note.notes}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleHistoryClick(note.id)}
                        className="p-1.5 text-slate-400 hover:text-primary hover:bg-blue-50 rounded transition-colors"
                        title="View addendum history"
                      >
                        <FiClock className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => startEditing(note)}
                        className="p-1.5 text-slate-400 hover:text-primary hover:bg-blue-50 rounded transition-colors"
                        title="Edit note"
                      >
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex gap-1">
                      <UserIcon size={12} />
                      <div >{note.addedBy}</div>
                    </div>
                    <span>•</span>
                    <div className="flex gap-1">
                      <ClockIcon size={12} />
                      <span>{FormatMomentDate(note.addedDate)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentNotes;
